'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { withLayout } from '../../shared/Styles.js'
import { Text, Heading, Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/index.js'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import type {
  ButtonFunctionType,
  CopyConfig,
  ProductCardData,
  ProductTab,
  StockDisplayMode,
  MediaShelfSemanticName,
} from './types.js'
import ProductCard from './ProductCard.js'
import { useExposure } from '../../hooks/useExposure.js'

const componentType = 'shelf'
const componentName = 'media_shelf'

const PrevIcon = ({ disabled }: { disabled: boolean }) => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-info-primary laptop:block lg-desktop:size-[56px] hidden size-[40px]"
  >
    <path
      d="M0 28C0 43.464 12.536 56 28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28Z"
      fill="currentColor"
      fillOpacity={disabled ? '0.2' : '0.6'}
    />
    <path d="M31 22L25 28L31 34" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const NextIcon = ({ disabled }: { disabled: boolean }) => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-info-primary laptop:block lg-desktop:size-[56px] hidden size-[40px]"
  >
    <path
      d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28Z"
      fill="currentColor"
      fillOpacity={disabled ? '0.2' : '0.6'}
    />
    <path d="M25 22L31 28L25 34" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * Code Channel 配置 - 折扣前缀
 */
export type CodeChannelConfig = string

/**
 * MediaShelf Context 配置接口
 */
export interface MediaShelfContextConfig {
  /** Code Channel 配置 - 折扣前缀 */
  codeChannel?: CodeChannelConfig
  /** 是否展示会员价格 */
  showMemberPrice?: boolean
  /** 是否展示折扣标签 */
  showDiscount?: boolean
  /** 是否展示标签系统 */
  showTags?: boolean
  /** 库存展示模式 */
  stockDisplayMode?: StockDisplayMode
  /** 库存阈值（当模式为 below-quantity 或 below-percentage 时使用） */
  stockThresholdValue?: number
  /** 是否展示原价（删除线价格） */
  showOriginalPrice?: boolean
  /** 用户状态 */
}

/**
 * MediaShelf Context 值接口
 */
interface MediaShelfContextValue extends MediaShelfContextConfig {
  /** 点击了解更多回调 */
  onLearnMore?: (product: ProductCardData) => void
  /** 点击立即购买回调 */
  onShopNow?: (product: ProductCardData) => void
  /** 点击加入购物车回调 */
  onAddToCart?: (product: ProductCardData) => void
  /** 语义化类名 */
  classNames?: Partial<Record<MediaShelfSemanticName, string>>
  /** 了解更多按钮文本 */
  secondaryButtonText?: string
  secondaryButtonFun?: ButtonFunctionType
  /** 立即购买按钮文本 */
  primaryButtonText?: string
  primaryButtonFun?: ButtonFunctionType
  /** 文案配置 */
  copy: CopyConfig
}

/**
 * MediaShelf Context
 */
const MediaShelfContext = React.createContext<MediaShelfContextValue | null>(null)

/**
 * 使用 MediaShelf Context 的 Hook
 */
export const useMediaShelfContext = () => {
  const context = React.useContext(MediaShelfContext)
  if (!context) {
    throw new Error('useMediaShelfContext must be used within an MediaShelfProvider')
  }
  return context
}

/**
 * MediaShelf 业务组件数据接口
 */
export interface MediaShelfData {
  theme?: 'light' | 'dark'
  /** 组件标题 */
  title?: string
  /** 组件描述 */
  description?: string
  /** 分组产品Tab列表 */
  productsTab?: ProductTab[]
  /** 是否显示 Tab（默认：true） */
  isShowTab?: boolean
  /** 了解更多按钮文本 */
  secondaryButtonText?: string
  secondaryButtonFun: ButtonFunctionType
  /** 立即购买按钮文本 */
  primaryButtonText?: string
  primaryButtonFun: ButtonFunctionType
  /** Code Channel 配置 - 折扣前缀 */
  codeChannel?: CodeChannelConfig
  /** 是否展示会员价格 */
  showMemberPrice?: boolean
  /** 是否展示折扣标签 */
  showDiscount?: boolean
  /** 是否展示标签系统 */
  showTags?: boolean
  /** 库存展示模式 */
  stockDisplayMode?: StockDisplayMode
  /** 库存阈值（当模式为 below-quantity 或 below-percentage 时使用） */
  stockThresholdValue?: number
  /**
   * 库存文本模板，支持 {count} 占位符
   */
  stockDisplayText?: string
  /** 是否展示原价（删除线价格） */
  showOriginalPrice?: boolean
  /** 文案配置 */
  copy?: CopyConfig
  /**
   * 产品列表（当不使用 Tab 分组时可直接传入产品列表）
   */
  productsCard: ProductCardData[]
}

export interface MediaShelfProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 业务数据 */
  data: MediaShelfData
  /** 点击了解更多回调 */
  onLearnMore?: (product: ProductCardData) => void
  /** 点击立即购买回调 */
  onShopNow?: (product: ProductCardData) => void
  /** 点击加入购物车回调 */
  onAddToCart?: (product: ProductCardData) => void
  /** 语义化类名 */
  classNames?: Partial<Record<MediaShelfSemanticName, string>>
}

/**
 * MediaShelf 复合组件类型定义
 */
export interface MediaShelfComponent extends React.ForwardRefExoticComponent<
  MediaShelfProps & React.RefAttributes<HTMLDivElement>
> {
  /** 独立的 ProductCard 组件 */
  ProductCard: typeof ProductCard
}

/**
 * 产品列表组件 - 使用 Swiper 横向滑动布局
 */
const ProductsList = React.forwardRef<
  HTMLDivElement,
  {
    products: ProductCardData[]
    className?: string
  }
>(({ products, className }, ref) => {
  const {
    copy,
    stockDisplayMode,
    stockThresholdValue,
    onLearnMore,
    onShopNow,
    onAddToCart,
    classNames,
    secondaryButtonText,
    primaryButtonText,
    secondaryButtonFun,
    primaryButtonFun,
    showOriginalPrice,
    showTags,
  } = useMediaShelfContext()
  // 生成唯一 ID，移除特殊字符以确保可用于 CSS 选择器
  const id = React.useId().replace(/:/g, '')
  // 预定义导航按钮选择器，避免多次拼接
  const nextButtonClass = `swiper-button-next-custom-${id}`
  const prevButtonClass = `swiper-button-prev-custom-${id}`

  // Swiper 导航状态
  const [swiperState, setSwiperState] = React.useState({
    isBeginning: true,
    isEnd: false,
  })

  // Swiper 实例引用
  const swiperRef = React.useRef<any>(null)

  // 计算并设置所有 slide 为最高高度
  const equalizeSlideHeights = React.useCallback(() => {
    const swiper = swiperRef.current
    if (!swiper?.slides || swiper.slides.length === 0) return

    // 先重置所有 slide 高度以获取真实内容高度
    swiper.slides.forEach((slide: HTMLElement) => {
      slide.style.height = 'auto'
    })

    // 计算最大高度
    // @ts-ignore
    const heights = Array.from(swiper.slides).map((slide: HTMLElement) => slide.scrollHeight)
    const maxHeight = Math.max(...heights)

    // 设置所有 slide 为最大高度
    if (maxHeight > 0) {
      swiper.slides.forEach((slide: HTMLElement) => {
        slide.style.height = `${maxHeight}px`
      })
    }
  }, [])

  // 监听窗口 resize 重新计算高度
  React.useEffect(() => {
    const handleResize = () => {
      // 使用 requestAnimationFrame 确保 DOM 更新完成
      requestAnimationFrame(equalizeSlideHeights)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [equalizeSlideHeights])

  if (products?.length === 0) {
    return null
  }

  return (
    <div ref={ref} className={className}>
      {/* Swiper 横向滑动布局 */}
      <div className="relative">
        <Swiper
          modules={[Navigation]}
          spaceBetween={16}
          slidesPerView="auto"
          navigation={{
            nextEl: `.${nextButtonClass}`,
            prevEl: `.${prevButtonClass}`,
          }}
          onSwiper={swiper => {
            swiperRef.current = swiper
            setSwiperState({
              isBeginning: swiper.isBeginning,
              isEnd: swiper.isEnd,
            })
            // 延迟执行以确保内容渲染完成
            setTimeout(equalizeSlideHeights, 100)
          }}
          onSlideChange={swiper => {
            setSwiperState({
              isBeginning: swiper.isBeginning,
              isEnd: swiper.isEnd,
            })
          }}
          className="!overflow-visible"
          breakpoints={{
            0: {
              spaceBetween: 12,
              slidesPerView: 1.2,
            },
            768: {
              spaceBetween: 12,
              slidesPerView: products.length <= 2 ? 2 : 2.3,
            },
            1024: {
              spaceBetween: 16,
              slidesPerView: Math.min(3, products.length),
            },
          }}
        >
          {products.map((product, index) => (
            <SwiperSlide
              key={product.shopify_id + '-' + product.sku || index}
              className="tablet:!max-w-[50%] overflow-hidden"
            >
              <ProductCard
                product={product}
                className="box-border w-full max-w-full"
                stockDisplayMode={stockDisplayMode}
                stockThresholdValue={stockThresholdValue}
                onLearnMore={onLearnMore}
                onShopNow={onShopNow}
                onAddToCart={onAddToCart}
                classNames={classNames}
                secondaryButtonText={secondaryButtonText}
                primaryButtonText={primaryButtonText}
                secondaryButtonFun={secondaryButtonFun}
                primaryButtonFun={primaryButtonFun}
                showOriginalPrice={showOriginalPrice}
                copy={copy}
                showTags={showTags}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 自定义导航按钮 */}
        <button
          className={cn(`${prevButtonClass} tablet:block absolute left-4 top-1/2 z-10 hidden -translate-y-1/2`, {
            'tablet:hidden': products.length <= 2,
            'laptop:hidden': products.length <= 3,
          })}
          aria-label={copy?.previousSlideLabel}
          disabled={swiperState.isBeginning}
        >
          <PrevIcon disabled={swiperState.isBeginning} />
        </button>
        <button
          className={cn(`${nextButtonClass} tablet:block absolute right-4 top-1/2 z-10 hidden -translate-y-1/2`, {
            'tablet:hidden': products.length <= 2,
            'laptop:hidden': products.length <= 3,
          })}
          aria-label={copy?.nextSlideLabel}
          disabled={swiperState.isEnd}
        >
          <NextIcon disabled={swiperState.isEnd} />
        </button>
      </div>
    </div>
  )
})

ProductsList.displayName = 'ProductsList'

/**
 * MediaShelf - 媒体货架
 *
 * @description 能够实时显示商品库存信息和会员价的货架，支持响应式布局和多种配置选项
 */
const MediaShelf = React.forwardRef<HTMLDivElement, MediaShelfProps>(
  ({ className, data, onLearnMore, onShopNow, onAddToCart, classNames, ...props }, ref) => {
    const boxRef = React.useRef<HTMLDivElement>(null)

    useExposure(boxRef, {
      componentType,
      componentName,
      componentTitle: data.title,
      componentDescription: data.description,
    })

    React.useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

    // 从 data 中解构配置项，并设置默认值
    const {
      theme = 'light',
      codeChannel,
      showMemberPrice = true,
      showDiscount = true,
      showTags = true,
      stockDisplayMode,
      stockThresholdValue,
      showOriginalPrice = true,
      secondaryButtonText,
      primaryButtonText,
      secondaryButtonFun,
      primaryButtonFun,
      isShowTab = true,
      copy,
    } = data

    // 构建 copy 配置，使用传入的 copy 配置
    const finalCopy: CopyConfig = copy || {}

    // 构建 Context 值
    const contextValue: MediaShelfContextValue = {
      codeChannel,
      showMemberPrice,
      showDiscount,
      showTags,
      stockDisplayMode,
      stockThresholdValue,
      showOriginalPrice,
      onLearnMore,
      onShopNow,
      onAddToCart,
      classNames,
      secondaryButtonText,
      secondaryButtonFun,
      primaryButtonText,
      primaryButtonFun,
      copy: finalCopy,
    }

    // 获取要显示的产品数据
    const hasTabData = data.productsTab && data.productsTab.length > 0
    const shouldShowTab = isShowTab && hasTabData

    return (
      <MediaShelfContext.Provider value={contextValue}>
        <div
          ref={boxRef}
          className={cn(
            'w-full',
            {
              'aiui-dark': theme === 'dark',
            },
            className,
            classNames?.root
          )}
          {...props}
        >
          {/* 标题区域 */}
          {data.title && (
            <Heading as="h2" html={data.title} size={4} className={cn('text-info-primary mb-4', classNames?.title)} />
          )}
          {data.description && (
            <Text
              size={3}
              as="p"
              html={data.description}
              className={cn('text-info-primary mb-6', classNames?.description)}
            />
          )}

          {/* 产品展示区域 */}

          <Tabs defaultValue={data.productsTab?.[0]?.id} className="w-full">
            {shouldShowTab && (
              <TabsList className="mb-6">
                {data.productsTab?.map(tab => (
                  <TabsTrigger key={'tab-' + tab.id} value={tab.id}>
                    {tab.tab}
                  </TabsTrigger>
                ))}
              </TabsList>
            )}
            {shouldShowTab ? (
              data.productsTab?.map(tab => (
                <TabsContent key={'tab-content-' + tab.id} value={tab.id}>
                  <ProductsList products={tab.data} />
                </TabsContent>
              ))
            ) : (
              <ProductsList products={data.productsCard} />
            )}
          </Tabs>
        </div>
      </MediaShelfContext.Provider>
    )
  }
)

MediaShelf.displayName = 'MediaShelf'

// 创建复合组件
const MediaShelfWithProductCard = MediaShelf as MediaShelfComponent
MediaShelfWithProductCard.ProductCard = ProductCard

const MediaShelfWrapped = withLayout(MediaShelfWithProductCard)

export default MediaShelfWrapped
